import { PossibleBigNumber } from '@ui/types/utility';
import { getEstPnl } from '@ui/utils/trade/getEstPnl';
import { getTakeProfitStopLossPercentage } from '@ui/utils/trade/getTakeProfitStopLossPercentage';
import { getTakeProfitStopLossPrice } from '@ui/utils/trade/getTakeProfitStopLossPrice';
import BigNumber from 'bignumber.js';

import { TpSlValueState } from '../types';

const getMinPositivePriceValue = (priceDisplayDecimals: number) =>
  new BigNumber(1).shiftedBy(-priceDisplayDecimals).toFixed(
    priceDisplayDecimals,
  );

const uniqueDescendingRates = (rates: number[]) =>
  Array.from(new Set(rates.filter(Number.isFinite))).sort((a, b) => b - a);

export const getPositiveTakeProfitState = ({
  requestedPnlRate,
  fallbackPnlRates,
  entryPrice,
  leverage,
  isLong,
  spreadPercentage,
  priceDisplayDecimals,
  collateralUsd,
}: {
  requestedPnlRate: number;
  fallbackPnlRates: number[];
  entryPrice: number;
  leverage: number;
  isLong: boolean;
  spreadPercentage: number;
  priceDisplayDecimals: number;
  collateralUsd: PossibleBigNumber;
}): TpSlValueState | undefined => {
  const roundingMode = isLong ? BigNumber.ROUND_FLOOR : BigNumber.ROUND_CEIL;
  const candidatePnlRates = uniqueDescendingRates([
    requestedPnlRate,
    ...fallbackPnlRates.filter((rate) => rate < requestedPnlRate),
  ]);

  for (const pnlRate of candidatePnlRates) {
    const targetPrice = getTakeProfitStopLossPrice({
      pnlRate,
      isTakeProfit: true,
      entryPrice,
      leverage,
      isLong,
      spreadPercentage,
    });
    const priceValue = new BigNumber(targetPrice).toFixed(
      priceDisplayDecimals,
      roundingMode,
    );

    if (Number(priceValue) > 0) {
      return {
        deltaPnl: getEstPnl({
          collateralUsd,
          targetPercentage: pnlRate,
          isLong,
          spreadPercentage,
        }),
        pnlPercentage: pnlRate,
        isPnlPercentage: true,
        priceValue,
        isLong,
      };
    }
  }

  const priceValue = getMinPositivePriceValue(priceDisplayDecimals);
  const pnlPercentage = getTakeProfitStopLossPercentage({
    targetPrice: Number(priceValue),
    entryPrice,
    leverage,
    isLong,
    spreadPercentage,
  });

  if (!Number.isFinite(pnlPercentage) || pnlPercentage <= 0) {
    return undefined;
  }

  return {
    deltaPnl: getEstPnl({
      collateralUsd,
      targetPercentage: pnlPercentage,
      isLong,
      spreadPercentage,
    }),
    pnlPercentage,
    isPnlPercentage: true,
    priceValue,
    isLong,
  };
};
